import * as React from 'react';
import { useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  Table,
  PropertyFilter,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState } from 'src/commons/EmptyState';
import { Link } from 'src/components/common/Link';
import { createSmusSsoGroupDetailLink } from 'src/routes';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import {
  mediumPageSizePreference,
  defaultWrapLinesPreference,
  i18nStrings,
  paginationLabels,
} from 'src/commons/tables';
import { compareBy } from 'src/components/utils/sorting';
import { PageHeader } from 'src/components/common/PageHeader';
import { AccountEntry } from '../utils/smusUtil';

export interface AccountsTableProps {
  accounts: AccountEntry[];
  loading: boolean;
  loadFailed: boolean;
  title: string;
  onAssociate: () => void;
  onEdit: (account: AccountEntry) => void;
}

export const AccountsTable = (props: AccountsTableProps) => {
  const [selectedItems, setSelectedItems] = useState<AccountEntry[]>([]);

  const columnDefinitions: TableProps.ColumnDefinition<AccountEntry>[] = [
    { id: 'accountId', header: 'Account ID', cell: (e) => e.accountId, sortingField: 'accountId' },
    { id: 'name', header: 'Name', cell: (e) => e.name, sortingField: 'name' },
    {
      id: 'groupName',
      header: 'Group',
      cell: (e) => <Link href={createSmusSsoGroupDetailLink(e.groupMetadataId)}>{e.groupName}</Link>,
      sortingField: 'groupName',
    },
    { id: 'createdBy', header: 'Associated by', cell: (e) => e.createdBy, sortingField: 'createdBy' },
    { id: 'createdAt', header: 'Associated at', cell: (e) => e.createdAt, sortingField: 'createdAt' },
  ];

  const contentDisplayOption: CollectionPreferencesProps.ContentDisplayOption[] = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header as string,
  }));

  const contentDisplayItem: CollectionPreferencesProps.ContentDisplayItem[] = [
    { id: 'accountId', visible: true },
    { id: 'name', visible: true },
    { id: 'groupName', visible: true },
    { id: 'createdBy', visible: true },
    { id: 'createdAt', visible: true },
  ];

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
    contentDisplay: contentDisplayItem,
  });

  const buttonOptions = () => {
    return [
      {
        text: 'Actions',
        onItemClick: ({ detail }) => {
          if (detail.id === 'edit' && selectedItems.length > 0) {
            props.onEdit(selectedItems[0]);
          }
        },
        items: [
          {
            id: 'edit',
            text: 'Edit',
            disabled: props.loading || props.loadFailed || selectedItems.length === 0,
          },
        ],
      },
      {
        text: 'Associate account',
        variant: 'primary',
        onItemClick: () => {
          props.onAssociate();
        },
        disabled: props.loading || props.loadFailed,
      },
    ];
  };

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(
    props.accounts,
    {
      filtering: {},
      pagination: { pageSize: preferences.pageSize },
      selection: {},
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: 'accountId',
            sortingComparator: compareBy('accountId', 'name', 'groupName'),
          },
        },
      },
      propertyFiltering: {
        empty: <EmptyState title='No accounts' subtitle='No accounts found.' />,
        filteringProperties: [
          {
            propertyLabel: 'Account ID',
            key: 'accountId',
            groupValuesLabel: 'Account IDs',
          },
          {
            propertyLabel: 'Group',
            key: 'groupName',
            groupValuesLabel: 'Groups',
          },
          {
            propertyLabel: 'Name',
            key: 'name',
            groupValuesLabel: 'Names',
          },
        ],
      },
    },
  );

  return (
    <Table
      {...collectionProps}
      loadingText='Loading accounts'
      loading={props.loading}
      columnDefinitions={columnDefinitions}
      items={items}
      wrapLines={preferences.wrapLines}
      selectionType='single'
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as AccountEntry[])}
      resizableColumns={true}
      columnDisplay={preferences.contentDisplay}
      empty={<EmptyState title='No accounts' subtitle='No accounts found.' />}
      header={<PageHeader buttons={buttonOptions()} counter={`(${props.accounts.length})`} header={props.title} />}
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      preferences={
        <CollectionPreferences
          title='Preferences'
          confirmLabel='Confirm'
          cancelLabel='Cancel'
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={mediumPageSizePreference}
          wrapLinesPreference={defaultWrapLinesPreference}
          contentDisplayPreference={{
            title: 'Account',
            options: contentDisplayOption,
          }}
        />
      }
      filter={
        <PropertyFilter
          {...propertyFilterProps}
          disabled={props.loading}
          i18nStrings={i18nStrings}
          countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
        />
      }
    />
  );
};
